import React from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';

import {
  VpPluginSortingSelect,
  VpPluginLogoCheckbox,
  VpIcon,
  VpConditionalUpgradeWrapper
} from '../..';
import VpAdjustPluginVideoButton from './VpAdjustPluginVideoButton';
import { StyledBox } from './styles';
import VpPluginTitleSection from './VpPluginTitleSection';
import VpPluginDescriptionSection from './VpPluginDescriptionSection';
import VpPluginBorderSection from './VpPluginBorderSection';
import VpPluginBoxShadowSection from './VpPluginBoxShadowSection';
import VpPluginCardInputs from '../../form-fields/plugin-details/VpPluginCardInputs';
import VpPluginDateSubmittedSection from './VpPluginDateSubmittedSection';
import {
  getAttachmentPluginsEnabled,
  getPluginManagementEnabled,
  getPluginRemoveLogoEnabled,
  getPluginStylesEditingEnabled,
  getUser
} from '../../../redux/modules/auth';
import {
  getIsOrganizationViewer,
  getSelectedOrganization
} from '../../../redux/modules/organization';
import { isFree } from '../../../helpers/plansHelper';
import { usePluginHelpers } from '../../../hooks';
import config from '../../../config';

const billingPageLink = <Link to="/billing">billing page</Link>;

const VpPluginDetailsFormComponent = ({
  pluginStylesEditingEnabled,
  pluginManagementEnabled,
  pluginRemoveLogoEnabled,
  isOrgViewer,
  user,
  organization,
  includeAttachments
}) => {
  const { hasPublishChanges, isDirty } = usePluginHelpers(config?.pluginFontSizeEnabled);

  const isFreeAccount = isFree({ user, organization });

  const showFeaturesChip =
    !isOrgViewer &&
    (!pluginStylesEditingEnabled || !pluginManagementEnabled || !pluginRemoveLogoEnabled);

  const upgradePrompt = isOrgViewer ? (
    'You do not have permissions to perform this action for this organization.'
  ) : (
    <span>
      Upgrade on the {billingPageLink} to customize your plugin even more with customized, titles,
      descriptions, theme colors, and more!
    </span>
  );

  const promptMessage = `You have ${
    hasPublishChanges ? 'unpublished' : 'unsaved'
  } changes, are you sure you want to leave this page?`;

  return (
    <>
      <Prompt
        message={(location, action) => {
          const dirtyForm = hasPublishChanges || isDirty;
          const showMessage =
            dirtyForm &&
            pluginManagementEnabled &&
            !isOrgViewer &&
            location?.state?.from !== 'PLUGIN_PAGE_ADJUST_VIDEOS' &&
            action !== 'REPLACE';

          return showMessage ? promptMessage : true;
        }}
      />
      <StyledBox>
        <Stack spacing="18px">
          <Stack direction="row" spacing="15px" alignItems="center">
            <Typography variant="formSectionTitle">Details</Typography>
            {showFeaturesChip && (
              <VpConditionalUpgradeWrapper condition={showFeaturesChip} label={upgradePrompt}>
                <Chip
                  icon={<VpIcon icon="Lock" />}
                  label={isFreeAccount ? 'Paid Features' : 'Premium Features'}
                  variant="primaryButton"
                />
              </VpConditionalUpgradeWrapper>
            )}
          </Stack>
          <VpAdjustPluginVideoButton includeAttachments={includeAttachments} />
          <VpPluginSortingSelect />
          <VpPluginLogoCheckbox />
          <Divider />
          <Typography variant="formSectionTitle">Text</Typography>
          <VpPluginTitleSection />
          <VpPluginDateSubmittedSection />
          <VpPluginDescriptionSection />
          <Divider />
          <Typography variant="formSectionTitle">Cards</Typography>
          <VpPluginCardInputs />
          <VpPluginBorderSection />
          <VpPluginBoxShadowSection />
        </Stack>
      </StyledBox>
    </>
  );
};

VpPluginDetailsFormComponent.propTypes = {
  pluginStylesEditingEnabled: PropTypes.bool,
  pluginManagementEnabled: PropTypes.bool,
  pluginRemoveLogoEnabled: PropTypes.bool,
  isOrgViewer: PropTypes.bool,
  user: PropTypes.object,
  organization: PropTypes.object,
  includeAttachments: PropTypes.bool
};

const mapStateToProps = (state) => ({
  pluginStylesEditingEnabled: getPluginStylesEditingEnabled(state),
  pluginManagementEnabled: getPluginManagementEnabled(state),
  pluginRemoveLogoEnabled: getPluginRemoveLogoEnabled(state),
  isOrgViewer: getIsOrganizationViewer(state),
  user: getUser(state),
  organization: getSelectedOrganization(state),
  includeAttachments: getAttachmentPluginsEnabled(state)
});

export const VpPluginDetailsForm = connect(mapStateToProps)(VpPluginDetailsFormComponent);

export default connect(mapStateToProps)(VpPluginDetailsFormComponent);



// WEBPACK FOOTER //
// ./src/vp-ui/widgets/VpPluginDetailsForm/VpPluginDetailsForm.jsx